import React, { useState, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { profileService } from '../../services/profileService';
import Button from '../ui/Button';
import LoadingSpinner from '../common/LoadingSpinner';

const ProfilePictureUpload = ({ profileData, userId, onUpdate }) => {
  const { isMobile, isTablet, getResponsiveValue } = useTheme();
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const sectionSpacing = getResponsiveValue({
    mobile: 'space-y-4',
    desktop: 'space-y-6'
  });

  const gridCols = getResponsiveValue({
    mobile: 'grid-cols-1',
    desktop: 'grid-cols-1 lg:grid-cols-2'
  });

  const gridGap = getResponsiveValue({
    mobile: 'gap-6',
    desktop: 'gap-8'
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-base',
    desktop: 'text-lg'
  });

  const subtitleSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const sectionTitleSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const textSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const imageSize = getResponsiveValue({
    mobile: 'w-32 h-32',
    desktop: 'w-48 h-48'
  });

  const dropZonePadding = getResponsiveValue({
    mobile: 'p-6',
    desktop: 'p-8'
  });

  const cardPadding = getResponsiveValue({
    mobile: 'p-3',
    desktop: 'p-4'
  });

  const buttonSpacing = getResponsiveValue({
    mobile: 'flex-col space-y-3',
    desktop: 'flex-row space-x-3'
  });

  const buttonSize = getResponsiveValue({
    mobile: 'w-full',
    desktop: 'flex-1'
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = profileService.validateProfilePicture(file);
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }

    setSelectedFile(file);
    setError(null);
    setSuccess(false);

    const preview = profileService.createImagePreview(file);
    setPreviewUrl(preview);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);

      const compressedFile = await profileService.compressImage(selectedFile);
      const downloadURL = await profileService.uploadProfilePicture(userId, compressedFile);
      
      onUpdate({ profilePicture: downloadURL });
      
      setSelectedFile(null);
      if (previewUrl) {
        profileService.cleanupImagePreview(previewUrl);
        setPreviewUrl(null);
      }
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    try {
      setRemoving(true);
      setError(null);

      await profileService.removeProfilePicture(userId);
      onUpdate({ profilePicture: null });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error removing profile picture:', error);
      setError(error.message);
    } finally {
      setRemoving(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setError(null);
    setSuccess(false);
    
    if (previewUrl) {
      profileService.cleanupImagePreview(previewUrl);
      setPreviewUrl(null);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      
      const fakeEvent = {
        target: {
          files: [file]
        }
      };
      
      handleFileSelect(fakeEvent);
    }
  };

  const currentPicture = profileData?.profilePicture;
  const displayImage = previewUrl || currentPicture || profileService.generateAvatarUrl(profileData);

  return (
    <div className={containerPadding}>
      {success && (
        <div className="mb-6 bg-success-subtle border border-border-primary rounded-xl p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className={`${textSize} font-medium text-success`}>
              Profile picture {selectedFile ? 'uploaded' : 'removed'} successfully!
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-error-subtle border border-border-error rounded-xl p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className={`${textSize} text-error`}>{error}</span>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h3 className={`${titleSize} font-semibold text-text-primary`}>Profile Picture</h3>
        <p className={`${subtitleSize} text-text-tertiary`}>
          Upload a new profile picture or remove the current one
        </p>
      </div>

      <div className={`grid ${gridCols} ${gridGap}`}>
        <div className={sectionSpacing}>
          <h4 className={`${sectionTitleSize} font-semibold text-text-primary uppercase tracking-wide`}>
            {previewUrl ? 'Preview' : 'Current Picture'}
          </h4>
          
          <div className="relative">
            <div className={`${imageSize} mx-auto rounded-2xl overflow-hidden bg-surface-secondary border-2 border-border-secondary`}>
              {displayImage ? (
                <img
                  src={displayImage}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-navigation">
                  <span className={`text-navigation-text ${getResponsiveValue({ mobile: 'text-2xl', desktop: 'text-4xl' })} font-semibold`}>
                    {profileService.getInitials(profileData.fullName)}
                  </span>
                </div>
              )}
            </div>
            
            {previewUrl && (
              <div className="absolute top-2 right-2">
                <div className="bg-info text-white text-xs px-2 py-1 rounded-lg">
                  Preview
                </div>
              </div>
            )}
          </div>

          {selectedFile && (
            <div className={`bg-info-subtle border border-border-primary rounded-xl ${cardPadding}`}>
              <div className={`${textSize} space-y-1`}>
                <div><span className="font-medium text-info">File:</span> <span className="text-text-primary">{selectedFile.name}</span></div>
                <div><span className="font-medium text-info">Size:</span> <span className="text-text-primary">{profileService.getFileSizeString(selectedFile.size)}</span></div>
                <div><span className="font-medium text-info">Type:</span> <span className="text-text-primary">{selectedFile.type}</span></div>
              </div>
            </div>
          )}
        </div>

        <div className={sectionSpacing}>
          <h4 className={`${sectionTitleSize} font-semibold text-text-primary uppercase tracking-wide`}>
            Upload New Picture
          </h4>
          
          <div
            className={`border-2 border-dashed border-border-secondary rounded-xl ${dropZonePadding} text-center hover:border-border-focus transition-colors cursor-pointer bg-surface-secondary hover:bg-surface-hover`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className={sectionSpacing}>
              <div className={`${getResponsiveValue({ mobile: 'w-10 h-10', desktop: 'w-12 h-12' })} bg-surface-primary rounded-xl flex items-center justify-center mx-auto`}>
                <svg className={`${getResponsiveValue({ mobile: 'w-5 h-5', desktop: 'w-6 h-6' })} text-text-disabled`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
                  />
                </svg>
              </div>
              <div>
                <p className={`${textSize} font-medium text-text-primary`}>
                  Drop your image here, or <span className="text-text-link underline">browse</span>
                </p>
                <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-tertiary mt-1`}>
                  Supports JPEG, PNG, WebP up to 5MB
                </p>
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className={`bg-surface-secondary rounded-xl ${cardPadding}`}>
            <h5 className={`${textSize} font-medium text-text-primary mb-2`}>Guidelines:</h5>
            <ul className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-tertiary space-y-1`}>
              <li>• Use a square image for best results</li>
              <li>• Minimum recommended size: 200x200 pixels</li>
              <li>• File size should be under 5MB</li>
              <li>• Supported formats: JPEG, PNG, WebP</li>
              <li>• Image will be automatically resized and optimized</li>
            </ul>
          </div>

          <div className="space-y-3">
            {selectedFile ? (
              <div className={`flex ${buttonSpacing}`}>
                <Button
                  onClick={handleUpload}
                  loading={uploading}
                  disabled={uploading}
                  className={buttonSize}
                >
                  {uploading ? 'Uploading...' : 'Upload Picture'}
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  disabled={uploading}
                  className={getResponsiveValue({ mobile: 'w-full', desktop: 'w-auto' })}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className={getResponsiveValue({ mobile: 'w-full', desktop: 'w-full' })}
              >
                Choose File
              </Button>
            )}

            {currentPicture && !selectedFile && (
              <Button
                onClick={handleRemove}
                variant="outline"
                loading={removing}
                disabled={removing}
                className={`${getResponsiveValue({ mobile: 'w-full', desktop: 'w-full' })} text-error border-border-error hover:bg-error-subtle`}
              >
                {removing ? 'Removing...' : 'Remove Current Picture'}
              </Button>
            )}
          </div>

          {(uploading || removing) && (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner size="sm" text={uploading ? 'Uploading image...' : 'Removing image...'} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureUpload;